import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Cross } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { href: "#accueil", label: "Accueil" },
  { href: "#direct", label: "Direct" },
  { href: "#cultes", label: "Cultes" },
  { href: "#apropos", label: "À Propos" },
  { href: "#evenements", label: "Événements" },
  { href: "#inscription", label: "Inscription" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar({ churchName, logoUrl }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-white/10 shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#accueil" data-testid="nav-logo" className="flex items-center gap-3 group">
            {logoUrl ? (
              <img src={logoUrl} alt={churchName} className="w-12 h-12 rounded-full object-cover ring-2 ring-gold/60 shadow-lg bg-white" />
            ) : (
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 border border-gold/40 text-gold shadow-lg backdrop-blur">
                <Cross className="w-5 h-5" strokeWidth={2.4} />
              </span>
            )}
            <div className="leading-tight">
              <div className="font-serif text-lg text-white font-semibold">{churchName}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Foi · Amour · Espérance</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="nav-link text-sm font-medium tracking-wide" data-testid={`nav-link-${n.label.toLowerCase()}`}>
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="#don" data-testid="nav-donate-btn">
              <Button className="bg-[#D4AF37] hover:bg-[#C59B27] text-white rounded-full px-6 shadow-md">
                Faire un don
              </Button>
            </a>
            <Link to="/admin/login" data-testid="nav-admin-link" className="text-xs text-white/70 hover:text-gold font-medium transition-colors">
              Admin
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 rounded-md text-white"
            onClick={() => setOpen(!open)}
            aria-label="Ouvrir le menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="block py-3 text-deepblue-900 font-medium border-b border-slate-100"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </a>
            ))}
            <a href="#don" onClick={() => setOpen(false)}>
              <Button className="w-full mt-3 bg-[#D4AF37] hover:bg-[#C59B27] text-white rounded-full">
                Faire un don
              </Button>
            </a>
            <Link to="/admin/login" className="block text-center py-3 text-sm text-slate-500">
              Espace administration
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
